import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./StationWiseSummary.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingOverlay from "react-loading-overlay";
import { SERVER_URL, USE_OVERLAY } from "../config/config";

const StationWiseSummary = ({ sessionObject }) => {
  const [overlay, setOverlay] = useState(false);
  const [data, setData] = useState([]);
  const [dateState, setDateState] = useState({
    date_from: new Date(),
    date_to: new Date(),
    in_stock: "2",
  });

  const roleId = sessionObject?.sessionVariables?.role_id;

  const fetchData = useCallback(() => {
    setOverlay(true);

    const payload = {
      date_dict: {
        date_from: dateState.date_from,
        date_to: dateState.date_to,
      },
      ...(dateState.in_stock === "1" && { filter_fields: { in_stock: 1 } }),
    };

    fetch(SERVER_URL + "/report/admin_lr", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        setData(data.data || []);
        setOverlay(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        setOverlay(false);
      });
  }, [dateState]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const mergeDuplicates = (data) => {
    const mergedData = {};

    data.forEach((item) => {
      const stationFrom = (item.station_from_name || "").trim().toUpperCase();
      const stationTo = (item.station_to_name || "").trim().toUpperCase();
      if (!stationFrom || !stationTo) return;

      const key = `${stationFrom}_${stationTo}`;

      if (!mergedData[key]) {
        mergedData[key] = {
          ...item,
          bilty_no_count: 1,
          no_of_package: Number(item.no_of_package || 0),
          charge_weight: Number(item.charge_weight || 0),
          bilty_charge: Number(item.bilty_charge || 0),
          hamali: Number(item.hamali || 0),
          freight: Number(item.freight || 0),
          door_del_charge: Number(item.door_del_charge || 0),
          collection_amount: Number(item.collection_amount || 0),
          other_amount: Number(item.other_amount || 0),
          tbb: item.pay_type === 4 ? Number(item.total_amount || 0) : 0,
          paid: item.pay_type === 2 ? Number(item.total_amount || 0) : 0,
          to_pay: item.pay_type === 1 ? Number(item.total_amount || 0) : 0,
        };
      } else {
        [
          "no_of_package",
          "charge_weight",
          "bilty_charge",
          "hamali",
          "freight",
          "door_del_charge",
          "collection_amount",
          "other_amount"
        ].forEach(
          (field) => (mergedData[key][field] += Number(item[field] || 0))
        );
        const amount = Number(item.total_amount || 0);
        if (item.pay_type === 1) mergedData[key].to_pay += amount;
        else if (item.pay_type === 2) mergedData[key].paid += amount;
        else if (item.pay_type === 4) mergedData[key].tbb += amount;

        mergedData[key].bilty_no_count++;
      }
    });

    return Object.values(mergedData);
  };

  const mergedData = useMemo(() => mergeDuplicates(data), [data]);

  const stationNames = useMemo(
    () => [...new Set(data.map((item) => item.station_from_name.toUpperCase()))],
    [data]
  );

  const calculateTotal = useCallback((filteredData) => {
    return filteredData.reduce(
      (total, item) => {
        [
          "no_of_package",
          "bilty_charge",
          "hamali",
          "freight",
          "charge_weight",
          "door_del_charge",
          "collection_amount",
          "other_amount"
        ].forEach((field) => (total[field] += Number(item[field] || 0)));

        total.tbb += Number(item.tbb || 0);
        total.paid += Number(item.paid || 0);
        total.to_pay += Number(item.to_pay || 0);
        total.bilty_no_count += Number(item.bilty_no_count);
        return total;
      },
      {
        no_of_package: 0,
        bilty_charge: 0,
        hamali: 0,
        freight: 0,
        charge_weight: 0,
        door_del_charge: 0,
        bilty_no_count: 0,
        collection_amount: 0,
        other_amount: 0,
        tbb: 0,
        paid: 0,
        to_pay: 0,
      }
    );
  }, []);

  return (
    <div className="summaryContainer">
      {USE_OVERLAY && (
        <LoadingOverlay
          active={overlay}
          spinner
          text="Loading your content..."
        />
      )}
      <div className="dateHolder">
        <div>
          From Date:{" "}
          <DatePicker
            dateFormat="dd-MM-yyyy"
            selected={dateState.date_from}
            onChange={(date) =>
              setDateState((prev) => ({ ...prev, date_from: date }))
            }
          />
        </div>
        <div>
          To Date:{" "}
          <DatePicker
            dateFormat="dd-MM-yyyy"
            selected={dateState.date_to}
            onChange={(date) =>
              setDateState((prev) => ({ ...prev, date_to: date }))
            }
          />
        </div>
        <div>
          <label>Stock?</label>
          <select
            onChange={(e) =>
              setDateState((prev) => ({ ...prev, in_stock: e.target.value }))
            }
            value={dateState.in_stock}
          >
            <option value="2">All</option>
            <option value="1">In Stock</option>
          </select>
        </div>
      </div>

      <div className="titleHolder">
        <span>Destination Wise Booking Summary Report</span>
      </div>

      <div className="summaryTable">
        {stationNames.map((stationName) => {
          const filteredData = mergedData.filter(
            (item) => item.station_from_name.toUpperCase() === stationName
          );
          const totals = calculateTotal(filteredData);

          return (
            <div key={stationName}>
              <div className="branchTitleHolder">
                <span className="branchTitle">Branch: {stationName}</span>
              </div>
              <table id="tabled">
                <thead>
                  <tr>
                    <th>Sr No.</th>
                    <th>Station To</th>
                    <th>Packages</th>
                    <th>Weight</th>
                    <th>No of Bilty</th>
                    {[1, 2].includes(roleId) && <th>Freight</th>} 
                    <th>Bilty Charge</th>
                    <th>Hamali</th>
                    <th>DD Charge</th>
                    <th>LC Charge</th>
                    <th>PF</th>
                    <th>To Pay</th>
                    <th>Paid</th>
                    {[1, 2].includes(roleId) && <th>TBB</th>} 
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.station_to_name.toUpperCase()}</td>
                      <td>{item.no_of_package}</td>
                      <td>{item.charge_weight}</td>
                      <td>{item.bilty_no_count}</td>
                      {[1, 2].includes(roleId) && <td>{item.freight.toFixed(2)}</td>} 
                      <td>{item.bilty_charge}</td>
                      <td>{item.hamali}</td>
                      <td>{item.door_del_charge}</td>
                      <td>{item.collection_amount}</td>
                      <td>{item.other_amount}</td>
                      <td>{item.to_pay}</td>
                      <td>{item.paid}</td>
                      {[1, 2].includes(roleId) && <td>{item.tbb}</td>} 
                    </tr>
                  ))}
                  <tr className="totalLabel">
                    <td></td>
                    <td>Total:</td>
                    <td>{totals.no_of_package}</td>
                    <td>{totals.charge_weight}</td>
                    <td>{totals.bilty_no_count}</td>
                    {[1, 2].includes(roleId) && <td>{totals.freight.toFixed(2)}</td>} 
                    <td>{totals.bilty_charge}</td>
                    <td>{totals.hamali}</td>
                    <td>{totals.door_del_charge}</td>
                    <td>{totals.collection_amount}</td>
                    <td>{totals.other_amount}</td>
                    <td>{totals.to_pay}</td>
                    <td>{totals.paid}</td>
                    {[1, 2].includes(roleId) && <td>{totals.tbb}</td>} 
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StationWiseSummary;
